import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack,
  Typography,
} from '@mui/material';
import moment from 'moment';
import {
  Form, useTranslate, required,
  useNotify,
} from 'react-admin';
import { useState, useEffect } from 'react';
import type { ExpiryTimeGranularity } from '@x-guard/xgac-types/xgac';
import { useFormContext } from 'react-hook-form';
import { ExpiryDateDisplay } from './ExpiryDateDisplay';
import { CertificationDocumentInput } from './inputs/CertificationDocumentInput';
import { BulkDocumentAssignmentStep } from './BulkDocumentUserAssignmentsModal';

type RenewNowStartDateKey = 'lastInspectionDate' | 'lastReceivedOn';

export type CertificationDocument = {
  rawFile?: File;
  src?: string;
  title?: string;
};

export type DocumentRecipient = {
  id: string;
  label: string;
};

type RenewNowFormValuesBase = {
  expiryDate: string;
  document?: CertificationDocument;
  documents?: CertificationDocument[];
  assignments?: Record<string, string>;
  documentsByAssetId?: Record<string, CertificationDocument>;
};

export type RenewNowFormValues =
  | ({ lastInspectionDate: string } & RenewNowFormValuesBase)
  | ({ lastReceivedOn: string } & RenewNowFormValuesBase);

const buildDocumentsByAssetId = (
  documents: CertificationDocument[] = [],
  assignments: Record<string, string> = {},
): Record<string, CertificationDocument> => {

  const result: Record<string, CertificationDocument> = {};
  documents.forEach((doc, index) => {

    const assetId = assignments[String(index)];
    if (assetId && doc.rawFile) {

      result[assetId] = doc;

    }

  });
  return result;

};

const validateBulkDocumentsUpload = (expectedCount: number) => (
  value: CertificationDocument[],
) => {

  const count = (value?.length ?? 0);

  if (!(count === expectedCount)) {

    return 'resources.assets.certifications.text.bulk_documents_count_mismatch';

  }
  return undefined;

};

const validateBulkAssignments = (
  assignments: Record<string, string> = {},
  recipients: DocumentRecipient[] = [],
): string | undefined => {

  const assignedIds = Object.values(assignments).filter(Boolean);
  const uniqueAssigned = new Set(assignedIds);

  if (uniqueAssigned.size !== assignedIds.length) {

    return 'resources.assets.certifications.text.bulk_documents_assignment_incomplete';

  }

  if (uniqueAssigned.size !== recipients.length) {

    return 'resources.assets.certifications.text.bulk_documents_assignment_incomplete';

  }
  return undefined;

};

const RenewNowNextButton = (props: {
  onNext: () => void;
  isBulkDocumentFlow: boolean;
  expectedDocumentsCount: number;
}) => {

  const translate = useTranslate();
  const { trigger, getValues, setValue } = useFormContext();
  const notify = useNotify();
  const handleNext = async () => {

    const fieldsToValidate = props.isBulkDocumentFlow
      ? ['lastReceivedOn', 'lastInspectionDate', 'expiryDate', 'documents']
      : ['lastReceivedOn', 'lastInspectionDate', 'expiryDate', 'document'];

    const valid = await trigger(fieldsToValidate, { shouldFocus: true });
    if (!valid) return;
    if (props.isBulkDocumentFlow) {

      const documents = (getValues('documents') ?? []) as CertificationDocument[];
      if (documents.length !== props.expectedDocumentsCount) {

        notify(translate('resources.assets.certifications.text.bulk_documents_count_mismatch'), { type: 'error' });
        return;

      }

    }

    setValue('assignments', {});
    props.onNext();

  };
  return (
    <Button type="button" variant="contained" onClick={handleNext}>
      {translate('ra.navigation.next')}
    </Button>
  );

};

export const RenewNowModal = (props: {
  open: boolean;
  onClose: () => void;
  onBack?: () => void;
  onSubmit: (values: RenewNowFormValues) => Promise<void>;
  timeGranularity?: ExpiryTimeGranularity;
  startDateSource?: RenewNowStartDateKey;
  expiryDateSource?: string;
  startDateLabel: string;
  expiryDateLabel: string;
  helperText: string;
  title: string;
  hasDocumentaryProofField?: boolean;
  bulkRecipients?: DocumentRecipient[];
}) => {

  const translate = useTranslate();
  const notify = useNotify();
  const [step, setStep] = useState<'basicDetails' | 'bulkDocumentAssignment'>('basicDetails');

  const startDateSource = props.startDateSource ?? 'lastInspectionDate';
  const expiryDateSource = props.expiryDateSource ?? 'expiryDate';
  const isBulkDocumentFlow = Boolean((props.bulkRecipients?.length ?? 0) > 1 && props.hasDocumentaryProofField);

  const defaultValues = {
    [startDateSource]: moment().format('YYYY-MM-DD'),
    [expiryDateSource]: props.timeGranularity
      ? moment().add(props.timeGranularity.value, props.timeGranularity.type).format('YYYY-MM-DD')
      : moment().format('YYYY-MM-DD'),
    documents: [],
    assignments: {},
  };

  const handleSubmit = async (values: Record<string, unknown>) => {

    const documents = values.documents as CertificationDocument[];
    const assignments = values.assignments as Record<string, string>;

    if (isBulkDocumentFlow) {

      const error = validateBulkAssignments(assignments, props.bulkRecipients);
      if (error) {

        notify(translate(error), { type: 'error' });
        return;

      }

    }

    const documentsByAssetId = isBulkDocumentFlow ? buildDocumentsByAssetId(documents, assignments) : undefined;

    const formValues: RenewNowFormValues = startDateSource === 'lastReceivedOn'
      ? {
        lastReceivedOn: values[startDateSource] as string,
        expiryDate: values[expiryDateSource] as string,
        document: values.document as { rawFile?: File } | undefined,
        documentsByAssetId,
      }
      : {
        lastInspectionDate: values[startDateSource] as string,
        expiryDate: values[expiryDateSource] as string,
        document: values.document as { rawFile?: File } | undefined,
        documentsByAssetId,
      };

    await props.onSubmit(formValues);

  };

  useEffect(() => {

    if (!props.open) setStep('basicDetails');

  }, [props.open]);

  return (
    <Dialog open={props.open} onClose={props.onClose} maxWidth="md" fullWidth>
      <Form id="renew-now-form" defaultValues={defaultValues} onSubmit={handleSubmit}>
        <DialogTitle>{translate(props.title)}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 3 }}>
            {step === 'basicDetails' && (
              <>
                <ExpiryDateDisplay
                  startDateSource={startDateSource}
                  expiryDateSource={expiryDateSource}
                  timeGranularity={props.timeGranularity}
                  startDateLabel={props.startDateLabel}
                  expiryDateLabel={props.expiryDateLabel}
                  helperText={props.helperText} usage="certifications"
                />
                {props.hasDocumentaryProofField && !isBulkDocumentFlow && (
                  <CertificationDocumentInput
                    source="document"
                    label={false}
                    validate={required()}
                    labelSingle={translate('resources.assets.certifications.text.drop_document_to_upload')}
                    accept={{ 'application/pdf': ['.pdf'], 'image/*': ['.jpg', '.jpeg', '.png'] }}
                  />
                )}
                {isBulkDocumentFlow && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      {translate('resources.assets.certifications.text.bulk_upload_documents_helper', {
                        smart_count: props.bulkRecipients?.length ?? 0,
                      })}
                    </Typography>
                    <CertificationDocumentInput
                      source="documents"
                      multiple
                      label={false}
                      validate={[
                        required(),
                        validateBulkDocumentsUpload(props.bulkRecipients?.length ?? 0),
                      ]}
                      labelMultiple={translate('resources.assets.certifications.text.drop_documents_to_upload')}
                      accept={{ 'application/pdf': ['.pdf'], 'image/*': ['.jpg', '.jpeg', '.png'] }}
                    />
                  </>
                )}
              </>
            )}
            {step === 'bulkDocumentAssignment' && props.bulkRecipients && (
              <BulkDocumentAssignmentStep recipients={props.bulkRecipients} />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          {step === 'bulkDocumentAssignment' ? (
            <Button type="button" onClick={() => setStep('basicDetails')}>
              {translate('ra.action.back')}
            </Button>
          ) : (
            props.onBack && (
              <Button onClick={props.onBack}>{translate('ra.action.back')}</Button>
            )
          )}
          <Button onClick={props.onClose}>{translate('ra.action.cancel')}</Button>
          {step === 'basicDetails' && isBulkDocumentFlow ? (
            <RenewNowNextButton
              onNext={() => setStep('bulkDocumentAssignment')}
              isBulkDocumentFlow
              expectedDocumentsCount={props.bulkRecipients?.length ?? 0}
            />
          ) : (
            <Button type="submit" form="renew-now-form" variant="contained">
              {translate('ra.action.confirm')}
            </Button>
          )}
        </DialogActions>
      </Form>
    </Dialog>
  );

};
